/**
 * Redirects Management Page
 * Configure query-to-URL redirections with AI-powered suggestions
 */

import { useState, useMemo } from 'react'
import {
  Plus,
  Trash2,
  Pencil,
  ExternalLink,
  Bot,
  Sparkles,
  ArrowRight,
  Search,
  AlertCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useRedirects,
  useCreateRedirect,
  useUpdateRedirect,
  useDeleteRedirect,
  useGenerateSuggestions,
  useApplySuggestions,
  type SearchRedirect,
  type RedirectSuggestion,
} from '../hooks/useRedirects'

// Match type labels
const MATCH_TYPE_LABELS: Record<string, string> = {
  exact: 'Exact Match',
  contains: 'Contains',
  starts_with: 'Starts With',
}

export default function RedirectsPage() {
  const { data: redirects, isLoading } = useRedirects()
  const createMutation = useCreateRedirect()
  const updateMutation = useUpdateRedirect()
  const deleteMutation = useDeleteRedirect()
  const generateMutation = useGenerateSuggestions()
  const applyMutation = useApplySuggestions()

  // State
  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [suggestionsOpen, setSuggestionsOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<RedirectSuggestion[]>([])
  const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set())
  const [filter, setFilter] = useState<'all' | 'enabled' | 'disabled' | 'ai'>('all')

  // Form state
  const [formData, setFormData] = useState({
    queryMatch: '',
    queryMatchType: 'exact' as 'exact' | 'contains' | 'starts_with',
    redirectUrl: '',
    description: '',
  })

  // Filter redirects
  const filteredRedirects = useMemo(() => {
    if (!redirects) return []
    return redirects.filter((r) => {
      if (filter === 'enabled') return r.enabled === 1
      if (filter === 'disabled') return r.enabled === 0
      if (filter === 'ai') return r.suggestedByAi === 1
      return true
    })
  }, [redirects, filter])

  // Stats
  const stats = useMemo(() => {
    if (!redirects) return { total: 0, enabled: 0, ai: 0, totalHits: 0 }
    return {
      total: redirects.length,
      enabled: redirects.filter((r) => r.enabled === 1).length,
      ai: redirects.filter((r) => r.suggestedByAi === 1).length,
      totalHits: redirects.reduce((sum, r) => sum + (r.hitCount || 0), 0),
    }
  }, [redirects])

  const resetForm = () => {
    setFormData({
      queryMatch: '',
      queryMatchType: 'exact',
      redirectUrl: '',
      description: '',
    })
  }

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync(formData)
      setCreateOpen(false)
      resetForm()
    } catch {
      // Error handled by mutation
    }
  }

  const handleEdit = (redirect: SearchRedirect) => {
    setEditingId(redirect.id)
    setFormData({
      queryMatch: redirect.queryMatch,
      queryMatchType: (redirect.queryMatchType as 'exact' | 'contains' | 'starts_with') || 'exact',
      redirectUrl: redirect.redirectUrl,
      description: redirect.description || '',
    })
  }

  const handleUpdate = async () => {
    if (!editingId) return
    try {
      await updateMutation.mutateAsync({ id: editingId, ...formData })
      setEditingId(null)
      resetForm()
    } catch {
      // Error handled by mutation
    }
  }

  const handleDelete = async () => {
    if (!deleteId) return
    try {
      await deleteMutation.mutateAsync(deleteId)
      setDeleteId(null)
    } catch {
      // Error handled by mutation
    }
  }

  const handleToggleEnabled = async (redirect: SearchRedirect) => {
    try {
      await updateMutation.mutateAsync({
        id: redirect.id,
        enabled: redirect.enabled !== 1,
      })
    } catch {
      // Error handled by mutation
    }
  }

  const handleGenerateSuggestions = async () => {
    try {
      const result = await generateMutation.mutateAsync()
      setSuggestions(result)
      setSelectedSuggestions(new Set(result.map((s) => s.query)))
      setSuggestionsOpen(true)
    } catch {
      // Error handled by mutation
    }
  }

  const handleApplySuggestions = async () => {
    const selected = suggestions.filter((s) => selectedSuggestions.has(s.query))
    if (selected.length === 0) return

    try {
      await applyMutation.mutateAsync(selected)
      setSuggestionsOpen(false)
      setSuggestions([])
      setSelectedSuggestions(new Set())
    } catch {
      // Error handled by mutation
    }
  }

  const toggleSuggestion = (query: string) => {
    setSelectedSuggestions((prev) => {
      const next = new Set(prev)
      if (next.has(query)) {
        next.delete(query)
      } else {
        next.add(query)
      }
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Redirects</h1>
          <p className="text-muted-foreground">
            Redirect search queries to specific URLs
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleGenerateSuggestions}
            disabled={generateMutation.isPending}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            {generateMutation.isPending ? 'Analyzing...' : 'AI Suggestions'}
          </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Redirect
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Redirect</DialogTitle>
                <DialogDescription>
                  Redirect search queries to a specific URL
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Query to match</Label>
                  <Input
                    placeholder="e.g., shipping"
                    value={formData.queryMatch}
                    onChange={(e) =>
                      setFormData({ ...formData, queryMatch: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Match type</Label>
                  <Select
                    value={formData.queryMatchType}
                    onValueChange={(v: 'exact' | 'contains' | 'starts_with') =>
                      setFormData({ ...formData, queryMatchType: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exact">Exact Match</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="starts_with">Starts With</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Redirect URL</Label>
                  <Input
                    placeholder="https://example.com/shipping-policy"
                    value={formData.redirectUrl}
                    onChange={(e) =>
                      setFormData({ ...formData, redirectUrl: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input
                    placeholder="Redirect shipping queries to shipping policy"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={
                    !formData.queryMatch ||
                    !formData.redirectUrl ||
                    createMutation.isPending
                  }
                >
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-card p-4 rounded-lg border">
          <div className="text-2xl font-bold">{stats.total}</div>
          <div className="text-sm text-muted-foreground">Total Redirects</div>
        </div>
        <div className="bg-card p-4 rounded-lg border">
          <div className="text-2xl font-bold">{stats.enabled}</div>
          <div className="text-sm text-muted-foreground">Enabled</div>
        </div>
        <div className="bg-card p-4 rounded-lg border">
          <div className="text-2xl font-bold">{stats.ai}</div>
          <div className="text-sm text-muted-foreground">AI Suggested</div>
        </div>
        <div className="bg-card p-4 rounded-lg border">
          <div className="text-2xl font-bold">{stats.totalHits}</div>
          <div className="text-sm text-muted-foreground">Total Hits</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Select value={filter} onValueChange={(v: typeof filter) => setFilter(v)}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Redirects</SelectItem>
            <SelectItem value="enabled">Enabled Only</SelectItem>
            <SelectItem value="disabled">Disabled Only</SelectItem>
            <SelectItem value="ai">AI Suggested</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {filteredRedirects.length === 0 ? (
        <div className="text-center py-12 border rounded-lg">
          <Search className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium mb-2">No redirects found</h3>
          <p className="text-muted-foreground mb-4">
            Create a redirect or use AI to suggest redirects based on zero-result searches.
          </p>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Redirect
          </Button>
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Query</TableHead>
                <TableHead>Match Type</TableHead>
                <TableHead>Redirect URL</TableHead>
                <TableHead>Hits</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRedirects.map((redirect) => (
                <TableRow key={redirect.id}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <code className="bg-muted px-2 py-1 rounded text-sm">
                        {redirect.queryMatch}
                      </code>
                      {redirect.suggestedByAi === 1 && (
                        <Badge variant="secondary" className="gap-1">
                          <Bot className="h-3 w-3" />
                          AI
                        </Badge>
                      )}
                    </div>
                    {redirect.description && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {redirect.description}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {MATCH_TYPE_LABELS[redirect.queryMatchType || 'exact']}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <a
                      href={redirect.redirectUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1 max-w-xs truncate"
                    >
                      {redirect.redirectUrl}
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{redirect.hitCount || 0}</span>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={redirect.enabled === 1}
                      onCheckedChange={() => handleToggleEnabled(redirect)}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(redirect)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(redirect.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingId} onOpenChange={(open) => !open && setEditingId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Redirect</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Query to match</Label>
              <Input
                value={formData.queryMatch}
                onChange={(e) =>
                  setFormData({ ...formData, queryMatch: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Match type</Label>
              <Select
                value={formData.queryMatchType}
                onValueChange={(v: 'exact' | 'contains' | 'starts_with') =>
                  setFormData({ ...formData, queryMatchType: v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="exact">Exact Match</SelectItem>
                  <SelectItem value="contains">Contains</SelectItem>
                  <SelectItem value="starts_with">Starts With</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Redirect URL</Label>
              <Input
                value={formData.redirectUrl}
                onChange={(e) =>
                  setFormData({ ...formData, redirectUrl: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingId(null)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Redirect?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The redirect will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AI Suggestions Dialog */}
      <Dialog open={suggestionsOpen} onOpenChange={setSuggestionsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5" />
              AI Redirect Suggestions
            </DialogTitle>
            <DialogDescription>
              Based on analysis of zero-result searches, here are suggested redirects.
              Select which ones to add (they will be disabled by default for review).
            </DialogDescription>
          </DialogHeader>

          {suggestions.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No suggestions found. This could mean:
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• No zero-result searches yet</li>
                <li>• All suggestions already have redirects</li>
                <li>• Store URL not configured</li>
              </ul>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
              {suggestions.map((suggestion) => {
                const isSelected = selectedSuggestions.has(suggestion.query)
                const confidencePercent = Math.round(suggestion.confidence * 100)

                return (
                  <div
                    key={suggestion.query}
                    className={`rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/50'
                    }`}
                    onClick={() => toggleSuggestion(suggestion.query)}
                  >
                    {/* Top section - Query and confidence */}
                    <div className="p-4 pb-3">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSuggestion(suggestion.query)}
                          className="h-5 w-5 mt-0.5 rounded border-2 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3 mb-1">
                            <span className="font-semibold text-base">
                              "{suggestion.query}"
                            </span>
                            <Badge
                              variant={confidencePercent >= 80 ? 'default' : 'secondary'}
                              className="flex-shrink-0"
                            >
                              {confidencePercent}% match
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {suggestion.searchCount} {suggestion.searchCount === 1 ? 'search' : 'searches'} with no results
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Bottom section - URL and reason */}
                    <div className="px-4 pb-4 pt-0 ml-8">
                      <div className="flex items-center gap-2 p-2 rounded bg-muted/50 mb-2">
                        <ArrowRight className="h-4 w-4 text-primary flex-shrink-0" />
                        <a
                          href={suggestion.suggestedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline truncate flex-1"
                          onClick={(e) => e.stopPropagation()}
                          title={suggestion.suggestedUrl}
                        >
                          {suggestion.suggestedUrl}
                        </a>
                        <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      </div>
                      <p className="text-xs text-muted-foreground italic">
                        {suggestion.reason}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSuggestionsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApplySuggestions}
              disabled={selectedSuggestions.size === 0 || applyMutation.isPending}
            >
              Add {selectedSuggestions.size} Redirect{selectedSuggestions.size !== 1 ? 's' : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
